// Client-side expression language for interfaces: a JSONLogic-style subset. Expressions are
// plain JSON ({ "op": [args] }), evaluated by a bounded interpreter with no access to
// prototypes, globals or the network. The backend never evaluates them; it re-checks every
// operation input it receives, so a wrong expression can only produce a rejected call.
import * as s from './schema-dsl.ts'
import { formatValue, VALUE_FORMATS, type ValueFormat } from './format.ts'

export const EXPR_SCOPES = ['state', 'derived', 'props', 'loop', 'index', 'row', 'item', 'acc', 'result', 'error', 'event', 'user'] as const
export type ExprScope = Partial<Record<typeof EXPR_SCOPES[number], unknown>>

export const EXPR_OPS = [
  'var', 'get', 'coalesce',
  '==', '!=', '<', '<=', '>', '>=',
  'and', 'or', '!', '!!', 'if',
  '+', '-', '*', '/', '%', 'min', 'max', 'round',
  'cat', 'lower', 'upper', 'trim', 'length', 'in',
  'sum', 'pluck', 'filter', 'map', 'some', 'all', 'reduce', 'keys', 'values', 'object', 'concat', 'append',
  'count', 'find', 'first', 'last', 'slice', 'sort', 'unique', 'group', 'index', 'join', 'split', 'cross',
  'fmt', 'today', 'now', 'date', 'addDays', 'diffDays', 'startOfWeek', 'weekday', 'minutesOfDay', 'timestamp'
] as const
export type ExprOp = typeof EXPR_OPS[number]

export const MAX_EXPR_STEPS = 20000
export const MAX_EXPR_DEPTH = 32
export const MAX_EXPR_STRING = 5000
export const MAX_EXPR_LIST = 1000
/** Arguments of one operator, and items of a literal list */
export const MAX_EXPR_ARGS = 40
/**
 * Steps all derives of one render share. Derives are recomputed on every render (each
 * keystroke), so this keeps the worst case below what 24 derives of MAX_EXPR_STEPS each could
 * cost before (480,000). Once used up, the remaining derives are null.
 */
export const MAX_DERIVE_STEPS = 400000
const TOO_MANY_ARGS = `an operator takes at most ${MAX_EXPR_ARGS} arguments, and a list at most ${MAX_EXPR_ARGS} items; build bigger values from parts (object of objects, concat of lists)`
const TOO_MANY_ITEMS = `has {got} items: an operator takes at most ${MAX_EXPR_ARGS} arguments, and a list at most ${MAX_EXPR_ARGS} items; build bigger values from parts (object of objects, concat of lists)`

/** fmt formats beyond the value formats: parts of a date or time */
export const DATE_FORMATS = ['time', 'weekday', 'weekday_short', 'daymonth', 'date_short', 'month', 'monthyear'] as const
export const FMT_FORMATS = [...VALUE_FORMATS, ...DATE_FORMATS] as const
export type FmtFormat = typeof FMT_FORMATS[number]

/**
 * The signed-in user as the host knows it, for display and for writing who did something.
 * Client-side data: anyone can send other values to the backend, so it never decides access.
 */
export interface ExprUser { id: number | string, name: string }

/** Only id and name, bounded; null when the host has no user (previews, run_max_spec, scheduled runs) */
export function exprUser (user: unknown): ExprUser | null {
  if (!user || typeof user !== 'object') return null
  const { id, name } = user as { id?: unknown, name?: unknown }
  const cleanId = typeof id === 'number' && Number.isFinite(id) ? id : typeof id === 'string' && id !== '' ? id.slice(0, 64) : null
  if (cleanId === null) return null
  return { id: cleanId, name: typeof name === 'string' ? name.slice(0, 200) : '' }
}

/** Locale and time zone for fmt and the date operators; hosts pass the shop's. now is for tests. */
export interface ExprEnv { locale?: string, timeZone?: string, now?: number }

// Scope, then identifiers or list indexes; never prototype members.
export const VAR_PATH_RE = /^(state|derived|props|loop|index|row|item|acc|result|error|event|user)(\.([A-Za-z_][A-Za-z0-9_]{0,63}|[0-9]{1,6})){0,12}$/
const BLOCKED_KEYS = new Set(['__proto__', 'prototype', 'constructor'])
const OBJECT_KEY_RE = /^[a-z][a-z0-9_]{0,31}$/

export type Expr = null | boolean | number | string | Expr[] | { [op: string]: Expr | Expr[] }

const scalar = s.anyOf([s.string({ max: 500 }), s.number(), s.boolean(), s.nullValue()])
const operand = () => s.anyOf([s.ref('Expr'), s.array(s.ref('Expr'), { max: MAX_EXPR_ARGS, maxMessage: TOO_MANY_ITEMS })])

let exprSchemaCache: s.Schema | null = null

/** Register as defs.Expr; reference with s.ref('Expr') */
export function exprSchema (): s.Schema {
  if (exprSchemaCache) return exprSchemaCache
  const props: Record<string, s.Schema> = {}
  for (const op of EXPR_OPS) props[op] = operand()
  props.var = s.anyOf([
    s.string({ max: 300, pattern: VAR_PATH_RE }),
    s.array(s.anyOf([s.string({ max: 300, pattern: VAR_PATH_RE }), scalar]), { min: 1, max: 2 })
  ], 'Path such as state.selected, row.id, item.price or result.rows, with an optional default')
  const call = s.object(props, {
    description: 'One operator with its arguments, e.g. {"==": [{"var": "state.status"}, "open"]}',
    refine: (v, _p, report) => {
      const keys = Object.keys(v)
      if (keys.length !== 1) report('', 'an expression object has exactly one operator')
      if (Array.isArray(v.var) && typeof v.var[0] !== 'string') report('var', 'the first item is the path')
      const op = keys[0] ?? ''
      const at = EXPR_BODY_ARGS[op]
      const raw = v[op]
      if (at && Array.isArray(raw) && raw.length > at.name && !validLoopName(raw[at.name])) {
        report(`${op}.${at.name}`, `names the ${op} body: an identifier such as "guide" (read as loop.guide), not an expression or a scope name`)
      }
    }
  })
  exprSchemaCache = s.anyOf([scalar, s.array(s.ref('Expr'), { max: MAX_EXPR_ARGS, maxMessage: TOO_MANY_ITEMS }), call], 'Client-side expression')
  return exprSchemaCache
}

// ---------------------------------------------------------------------------
// Static analysis

/** Operators whose second argument is evaluated once per element, with item and index in scope */
export const EXPR_LAMBDA_OPS: readonly string[] = ['filter', 'map', 'some', 'all', 'count', 'find', 'reduce']

/**
 * Operators with a per-element body: where the body is and where its optional name goes. A
 * name binds loop.<name> to the element, so a nested body can still read the outer one.
 */
export const EXPR_BODY_ARGS: Readonly<Record<string, { body: number, name: number }>> = {
  filter: { body: 1, name: 2 },
  map: { body: 1, name: 2 },
  some: { body: 1, name: 2 },
  all: { body: 1, name: 2 },
  count: { body: 1, name: 2 },
  find: { body: 1, name: 2 },
  reduce: { body: 1, name: 3 },
  index: { body: 2, name: 3 }
}

/** A body name: an identifier that is not a scope name */
export const LOOP_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/
export function validLoopName (name: unknown): name is string {
  return typeof name === 'string' && LOOP_NAME_RE.test(name) && !(EXPR_SCOPES as readonly string[]).includes(name) && !BLOCKED_KEYS.has(name)
}

/**
 * lambda: inside a per-element body (item and index bound); reduce: inside a reduce body (acc
 * bound); loops: names of the enclosing named bodies (loop.<name> bound)
 */
export interface ExprRef { path: string, scope: string, lambda: boolean, reduce: boolean, loops: string[] }

interface ExprWithin { lambda: boolean, reduce: boolean, loops: string[] }

/** Every var path the expression reads, so lint can check state names and scopes. */
export function exprVars (expr: unknown, out: ExprRef[] = [], within: ExprWithin = { lambda: false, reduce: false, loops: [] }): ExprRef[] {
  if (Array.isArray(expr)) {
    for (const e of expr) exprVars(e, out, within)
  } else if (expr && typeof expr === 'object') {
    for (const [op, arg] of Object.entries(expr)) {
      if (op === 'var') {
        const path = Array.isArray(arg) ? arg[0] : arg
        if (typeof path === 'string') out.push({ path, scope: path.split('.')[0] ?? '', ...within })
        if (Array.isArray(arg)) exprVars(arg.slice(1), out, within)
      } else if (EXPR_BODY_ARGS[op] && Array.isArray(arg)) {
        const at = EXPR_BODY_ARGS[op]!
        const name = arg[at.name]
        const body: ExprWithin = {
          lambda: within.lambda || EXPR_LAMBDA_OPS.includes(op),
          reduce: within.reduce || op === 'reduce',
          loops: validLoopName(name) ? [...within.loops, name] : within.loops
        }
        arg.forEach((a, i) => { if (i !== at.name) exprVars(a, out, i === at.body ? body : within) })
      } else {
        exprVars(arg, out, within)
      }
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Evaluation

export class ExprError extends Error {}

/** Steps several expressions share, e.g. all derives of one render (MAX_DERIVE_STEPS) */
export interface ExprBudget { left: number }

interface EvalCtx { steps: number, depth: number, env: ExprEnv, budget?: ExprBudget }

function own (obj: unknown, key: string): unknown {
  if (BLOCKED_KEYS.has(key) || obj === null || typeof obj !== 'object') return undefined
  return Object.prototype.hasOwnProperty.call(obj, key) ? (obj as Record<string, unknown>)[key] : undefined
}

export function readPath (scope: ExprScope, path: string): unknown {
  if (!VAR_PATH_RE.test(path)) return undefined
  let cur: unknown = scope
  for (const seg of path.split('.')) {
    cur = own(cur, seg)
    if (cur === undefined) return undefined
  }
  return cur
}

export function truthy (v: unknown): boolean {
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'number') return v !== 0 && !Number.isNaN(v)
  return !!v
}

function num (v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}

function str (v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'object') return ''
  return String(v).slice(0, MAX_EXPR_STRING)
}

function loose (a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a === null || a === undefined || b === null || b === undefined) return (a ?? null) === (b ?? null)
  const na = num(a)
  const nb = num(b)
  if (na !== null && nb !== null && (typeof a === 'number' || typeof b === 'number')) return na === nb
  return false
}

function list (v: unknown): unknown[] {
  return Array.isArray(v) ? v.slice(0, MAX_EXPR_LIST) : []
}

function round (v: number, digits: number): number {
  const f = 10 ** Math.max(0, Math.min(6, Math.trunc(digits)))
  return Math.round(v * f) / f
}

function compare (op: string, values: unknown[]): boolean {
  const nums = values.map(num)
  if (nums.some(n => n === null)) {
    if (values.every(v => typeof v === 'string')) {
      const strs = values as string[]
      for (let i = 1; i < strs.length; i++) {
        const c = strs[i - 1]!.localeCompare(strs[i]!)
        if (!(op === '<' ? c < 0 : op === '<=' ? c <= 0 : op === '>' ? c > 0 : c >= 0)) return false
      }
      return strs.length > 1
    }
    return false
  }
  for (let i = 1; i < nums.length; i++) {
    const a = nums[i - 1]!
    const b = nums[i]!
    if (!(op === '<' ? a < b : op === '<=' ? a <= b : op === '>' ? a > b : a >= b)) return false
  }
  return nums.length > 1
}

// ---------------------------------------------------------------------------
// Dates. Unix timestamps (seconds) read in the env time zone. ISO dates and wall-clock ISO
// datetimes without an offset are calendar values: they read as UTC so they never shift a day.

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const ISO_LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?$/
const DAY_MS = 86400000

function validZone (zone: string | undefined): string | undefined {
  if (!zone) return undefined
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone })
    return zone
  } catch {
    return undefined
  }
}

function toInstant (v: unknown, env: ExprEnv): { date: Date, zone: string | undefined } | null {
  if (typeof v === 'number' && Number.isFinite(v)) return { date: new Date(v < 1e12 ? v * 1000 : v), zone: validZone(env.timeZone) }
  if (typeof v !== 'string') return null
  let m = ISO_DATE_RE.exec(v)
  if (m) return { date: new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!)), zone: 'UTC' }
  m = ISO_LOCAL_RE.exec(v)
  if (m) return { date: new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +(m[6] ?? 0))), zone: 'UTC' }
  if (!/^\d{4}-\d{2}-\d{2}T/.test(v)) return null
  const t = Date.parse(v)
  return Number.isNaN(t) ? null : { date: new Date(t), zone: validZone(env.timeZone) }
}

function intlLocale (env: ExprEnv): string {
  try {
    return Intl.getCanonicalLocales((env.locale ?? 'en_US').replace('_', '-'))[0] ?? 'en-US'
  } catch {
    return 'en-US'
  }
}

/** ISO date (YYYY-MM-DD) of a value in the env time zone */
function isoDate (v: unknown, env: ExprEnv): string | null {
  const at = toInstant(v, env)
  if (!at || Number.isNaN(at.date.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: at.zone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(at.date)
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

function dateUtc (iso: string | null): number | null {
  const m = iso ? ISO_DATE_RE.exec(iso) : null
  return m ? Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!) : null
}

const utcToIso = (ms: number) => new Date(ms).toISOString().slice(0, 10)

const TIME_OF_DAY_RE = /^(\d{1,2})[:.](\d{2})$/

/** Wall-clock parts of an instant in a zone, as if that wall clock were UTC (ms) */
function wallClockUtc (ms: number, zone: string | undefined): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
  }).formatToParts(new Date(ms))
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0)
  return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
}

/** Minutes since local midnight: Unix timestamps in the env zone, wall-clock ISO values as written */
function minutesOfDay (v: unknown, env: ExprEnv): number | null {
  const at = toInstant(v, env)
  if (!at || Number.isNaN(at.date.getTime())) return null
  const wall = wallClockUtc(at.date.getTime(), at.zone)
  return Math.floor((((wall % DAY_MS) + DAY_MS) % DAY_MS) / 60000)
}

/** 'HH:MM' (or H.MM) or minutes, 0 to 24:00 inclusive, as minutes of the day */
function timeMinutes (v: unknown): number | null {
  if (typeof v === 'number') return Number.isInteger(v) && v >= 0 && v <= 1440 ? v : null
  const m = typeof v === 'string' ? TIME_OF_DAY_RE.exec(v.trim()) : null
  if (!m) return null
  const h = +m[1]!
  const min = +m[2]!
  if (min > 59 || h > 24 || (h === 24 && min > 0)) return null
  return h * 60 + min
}

/**
 * Unix seconds of a date's wall-clock time in the env zone. At a DST change a time that does
 * not exist (clocks jump forward) moves forward by the gap, e.g. 03:30 becomes 04:30, and a time
 * that occurs twice (clocks go back) is the earlier of the two, as JavaScript Temporal does.
 */
function zonedTimestamp (date: unknown, time: unknown, env: ExprEnv): number | null {
  const day = dateUtc(isoDate(date, env))
  const minutes = timeMinutes(time)
  if (day === null || minutes === null) return null
  const wall = day + minutes * 60000
  const zone = validZone(env.timeZone)
  const offsetAt = (ms: number) => wallClockUtc(ms, zone) - ms
  const before = offsetAt(wall - DAY_MS)
  const after = offsetAt(wall + DAY_MS)
  const fits = [wall - Math.max(before, after), wall - Math.min(before, after)].filter(t => offsetAt(t) === wall - t)
  const instant = fits.length ? Math.min(...fits) : wall - before
  return Math.floor(instant / 1000)
}

const DATE_PART_OPTIONS: Record<typeof DATE_FORMATS[number], Intl.DateTimeFormatOptions> = {
  time: { hour: '2-digit', minute: '2-digit' },
  weekday: { weekday: 'long' },
  weekday_short: { weekday: 'short' },
  daymonth: { day: 'numeric', month: 'numeric' },
  date_short: { day: 'numeric', month: 'numeric', year: 'numeric' },
  month: { month: 'long' },
  monthyear: { month: 'long', year: 'numeric' }
}

/** fmt outside expressions, e.g. keyvalue items with a format */
export function formatExprValue (v: unknown, format: unknown, currency: unknown, env: ExprEnv = {}): string {
  return fmt(v, format, currency, env)
}

function fmt (v: unknown, format: unknown, currency: unknown, env: ExprEnv): string {
  if (v === null || v === undefined || v === '') return ''
  const f = typeof format === 'string' ? format : ''
  const dateParts = (DATE_FORMATS as readonly string[]).includes(f) ? DATE_PART_OPTIONS[f as typeof DATE_FORMATS[number]] : null
  if (dateParts || f === 'date' || f === 'datetime') {
    const at = toInstant(v, env)
    if (!at || Number.isNaN(at.date.getTime())) return str(v)
    const options: Intl.DateTimeFormatOptions = dateParts ?? (f === 'date' ? { dateStyle: 'medium' } : { dateStyle: 'medium', timeStyle: 'short' })
    return new Intl.DateTimeFormat(intlLocale(env), { ...options, timeZone: at.zone }).format(at.date)
  }
  const valueFormat = (VALUE_FORMATS as readonly string[]).includes(f) ? f as ValueFormat : undefined
  return formatValue(v, { format: valueFormat, currency: typeof currency === 'string' ? currency : undefined, locale: env.locale })
}

/** Numbers before text, empty values last */
function sortKey (v: unknown): [number, number | string] {
  if (v === null || v === undefined || v === '') return [2, 0]
  return typeof v === 'number' ? [0, v] : [1, str(v).toLowerCase()]
}

function evaluate (expr: unknown, scope: ExprScope, ctx: EvalCtx): unknown {
  if (++ctx.steps > MAX_EXPR_STEPS) throw new ExprError('expression is too expensive')
  if (ctx.budget && --ctx.budget.left < 0) throw new ExprError(`the derives together take more than ${MAX_DERIVE_STEPS} steps`)
  if (expr === null || typeof expr !== 'object') return expr
  if (ctx.depth >= MAX_EXPR_DEPTH) throw new ExprError(`expression nested deeper than ${MAX_EXPR_DEPTH} levels`)
  ctx.depth++
  try {
    return evaluateNode(expr, scope, ctx)
  } finally {
    ctx.depth--
  }
}

function evaluateNode (expr: object, scope: ExprScope, ctx: EvalCtx): unknown {
  if (Array.isArray(expr)) {
    if (expr.length > MAX_EXPR_ARGS) throw new ExprError(`a list has ${expr.length} items; ${TOO_MANY_ARGS}`)
    return expr.map(e => evaluate(e, scope, ctx))
  }
  const keys = Object.keys(expr)
  if (keys.length !== 1) throw new ExprError('an expression object has exactly one operator')
  const op = keys[0] as string
  if (!(EXPR_OPS as readonly string[]).includes(op)) throw new ExprError(`unknown operator ${op}`)
  const raw = (expr as Record<string, unknown>)[op]
  if (Array.isArray(raw) && raw.length > MAX_EXPR_ARGS) throw new ExprError(`${op} has ${raw.length} arguments; ${TOO_MANY_ARGS}`)
  const rawArgs = Array.isArray(raw) ? raw : [raw]
  const arg = (i: number) => evaluate(rawArgs[i], scope, ctx)
  const args = () => rawArgs.map((_a, i) => arg(i))
  // A per-element body sees the element as item and, when the call names it, as loop.<name>
  const at = EXPR_BODY_ARGS[op]
  const loopName = at && rawArgs.length > at.name ? rawArgs[at.name] : undefined
  if (loopName !== undefined && !validLoopName(loopName)) throw new ExprError(`the name of a ${op} body is an identifier such as "guide", not a scope name`)
  const outerLoop = scope.loop && typeof scope.loop === 'object' && !Array.isArray(scope.loop) ? scope.loop as Record<string, unknown> : {}
  const bodyScope = (item: unknown, extra: ExprScope): ExprScope => loopName === undefined
    ? { ...scope, ...extra, item }
    : { ...scope, ...extra, item, loop: { ...outerLoop, [loopName as string]: item } }

  switch (op as ExprOp) {
    case 'var': {
      const path = arg(0)
      const value = typeof path === 'string' ? readPath(scope, path) : undefined
      return value === undefined ? (rawArgs.length > 1 ? arg(1) : null) : value
    }
    case 'get': {
      const key = str(arg(1))
      const value = own(arg(0), key)
      return value === undefined ? (rawArgs.length > 2 ? arg(2) : null) : value
    }
    case 'coalesce': {
      for (let i = 0; i < rawArgs.length; i++) {
        const v = arg(i)
        if (v !== null && v !== undefined && v !== '') return v
      }
      return null
    }
    case '==': return loose(arg(0), arg(1))
    case '!=': return !loose(arg(0), arg(1))
    case '<': case '<=': case '>': case '>=': return compare(op, args())
    case 'and': {
      let v: unknown = true
      for (let i = 0; i < rawArgs.length; i++) {
        v = arg(i)
        if (!truthy(v)) return v
      }
      return v
    }
    case 'or': {
      let v: unknown = false
      for (let i = 0; i < rawArgs.length; i++) {
        v = arg(i)
        if (truthy(v)) return v
      }
      return v
    }
    case '!': return !truthy(arg(0))
    case '!!': return truthy(arg(0))
    case 'if': {
      let i = 0
      for (; i + 1 < rawArgs.length; i += 2) {
        if (truthy(arg(i))) return arg(i + 1)
      }
      return i < rawArgs.length ? arg(i) : null
    }
    case '+': {
      const nums = args().map(num)
      return nums.some(n => n === null) ? null : (nums as number[]).reduce((a, b) => a + b, 0)
    }
    case '*': {
      const nums = args().map(num)
      return nums.some(n => n === null) || !nums.length ? null : (nums as number[]).reduce((a, b) => a * b, 1)
    }
    case '-': {
      const a = num(arg(0))
      if (rawArgs.length === 1) return a === null ? null : -a
      const b = num(arg(1))
      return a === null || b === null ? null : a - b
    }
    case '/': case '%': {
      const a = num(arg(0))
      const b = num(arg(1))
      if (a === null || b === null || b === 0) return null
      return op === '/' ? a / b : a % b
    }
    case 'min': case 'max': {
      const nums = args().flatMap(v => Array.isArray(v) ? list(v) : [v]).map(num).filter((n): n is number => n !== null)
      if (!nums.length) return null
      return op === 'min' ? Math.min(...nums) : Math.max(...nums)
    }
    case 'round': {
      const v = num(arg(0))
      return v === null ? null : round(v, num(arg(1)) ?? 0)
    }
    case 'cat': return args().map(str).join('').slice(0, MAX_EXPR_STRING)
    case 'lower': return str(arg(0)).toLowerCase()
    case 'upper': return str(arg(0)).toUpperCase()
    case 'trim': return str(arg(0)).trim()
    case 'length': {
      const v = arg(0)
      if (Array.isArray(v)) return v.length
      if (v && typeof v === 'object') return Object.keys(v).length
      return str(v).length
    }
    case 'in': {
      const needle = arg(0)
      const haystack = arg(1)
      if (Array.isArray(haystack)) return list(haystack).some(v => loose(v, needle))
      if (typeof haystack === 'string') return haystack.includes(str(needle))
      return false
    }
    case 'sum': {
      const field = rawArgs.length > 1 ? str(arg(1)) : null
      let total = 0
      for (const item of list(arg(0))) {
        const n = num(field === null ? item : own(item, field))
        if (n !== null) total += n
      }
      return total
    }
    case 'pluck': {
      const field = str(arg(1))
      return list(arg(0)).map(item => own(item, field) ?? null)
    }
    case 'filter': case 'map': case 'some': case 'all': {
      const items = list(arg(0))
      const body = rawArgs[1]
      const results = items.map((item, index) => evaluate(body, bodyScope(item, { index }), ctx))
      if (op === 'map') return results
      if (op === 'filter') return items.filter((_v, i) => truthy(results[i]))
      return op === 'some' ? results.some(truthy) : items.length > 0 && results.every(truthy)
    }
    case 'reduce': {
      // The body sees the running value as acc and the element as item; it starts from initial
      const items = list(arg(0))
      let acc = rawArgs.length > 2 ? arg(2) : null
      items.forEach((item, index) => {
        acc = evaluate(rawArgs[1], bodyScope(item, { acc, index }), ctx)
      })
      return acc ?? null
    }
    case 'keys': case 'values': {
      const v = arg(0)
      if (!v || typeof v !== 'object' || Array.isArray(v)) return []
      const keys = Object.keys(v).filter(k => !BLOCKED_KEYS.has(k)).slice(0, MAX_EXPR_LIST)
      return op === 'keys' ? keys : keys.map(k => (v as Record<string, unknown>)[k])
    }
    case 'object': {
      const out: Record<string, unknown> = {}
      for (let i = 0; i + 1 < rawArgs.length; i += 2) {
        const key = arg(i)
        if (typeof key !== 'string' || !OBJECT_KEY_RE.test(key)) throw new ExprError('object keys are lowercase identifiers')
        out[key] = arg(i + 1)
      }
      return out
    }
    case 'concat': {
      // Lists are joined; any other argument is added as one element
      const out: unknown[] = []
      for (let i = 0; i < rawArgs.length && out.length < MAX_EXPR_LIST; i++) {
        const v = arg(i)
        if (Array.isArray(v)) out.push(...list(v))
        else out.push(v ?? null)
      }
      return out.slice(0, MAX_EXPR_LIST)
    }
    case 'append': {
      const items = list(arg(0))
      return items.length < MAX_EXPR_LIST ? [...items, arg(1) ?? null] : items
    }
    case 'count': {
      const items = list(arg(0))
      if (rawArgs.length < 2) return items.length
      return items.filter((item, index) => truthy(evaluate(rawArgs[1], bodyScope(item, { index }), ctx))).length
    }
    case 'find': {
      const items = list(arg(0))
      for (let index = 0; index < items.length; index++) {
        const item = items[index]
        if (truthy(evaluate(rawArgs[1], bodyScope(item, { index }), ctx))) return item
      }
      return null
    }
    case 'first': case 'last': {
      const v = arg(0)
      if (typeof v === 'string') return op === 'first' ? v.slice(0, 1) : v.slice(-1)
      const items = list(v)
      return (op === 'first' ? items[0] : items[items.length - 1]) ?? null
    }
    case 'slice': {
      const v = arg(0)
      const start = num(arg(1)) ?? 0
      const end = rawArgs.length > 2 ? num(arg(2)) ?? undefined : undefined
      return typeof v === 'string' ? v.slice(start, end) : list(v).slice(start, end)
    }
    case 'sort': {
      const field = rawArgs.length > 1 ? arg(1) : null
      const desc = rawArgs.length > 2 && str(arg(2)).toLowerCase() === 'desc'
      const keyed = list(arg(0)).map((item, i) => ({
        item, i, key: sortKey(typeof field === 'string' && field !== '' ? own(item, field) : item)
      }))
      keyed.sort((a, b) => {
        if (a.key[0] !== b.key[0]) return a.key[0] - b.key[0]
        if (a.key[0] === 2) return a.i - b.i
        const c = typeof a.key[1] === 'number' && typeof b.key[1] === 'number'
          ? a.key[1] - b.key[1]
          : String(a.key[1]).localeCompare(String(b.key[1]))
        return (desc ? -c : c) || a.i - b.i
      })
      return keyed.map(k => k.item)
    }
    case 'unique': {
      const field = rawArgs.length > 1 ? str(arg(1)) : null
      const seen = new Set<string>()
      return list(arg(0)).filter(item => {
        const v = field === null ? item : own(item, field)
        const key = v !== null && typeof v === 'object' ? JSON.stringify(v) : `${typeof v}:${String(v)}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    }
    case 'group': {
      const field = str(arg(1))
      const groups = new Map<string, { key: unknown, items: unknown[] }>()
      for (const item of list(arg(0))) {
        const v = own(item, field) ?? null
        const k = `${typeof v}:${String(v)}`
        if (!groups.has(k)) groups.set(k, { key: v, items: [] })
        groups.get(k)!.items.push(item)
      }
      return [...groups.values()].map(g => ({ key: g.key, items: g.items, count: g.items.length }))
    }
    case 'index': {
      // A lookup map from a list: key field of each item to the item, or to a value computed per item
      const field = str(arg(1))
      const out: Record<string, unknown> = {}
      for (const item of list(arg(0))) {
        const k = own(item, field)
        if (typeof k !== 'string' && typeof k !== 'number') continue
        const key = String(k)
        if (BLOCKED_KEYS.has(key) || key in out) continue
        out[key] = rawArgs.length > 2 ? evaluate(rawArgs[2], bodyScope(item, {}), ctx) : item
      }
      return out
    }
    case 'cross': {
      // Every pair of elements as { a, b }, a-major; capped at the list limit and counted as steps
      const as = list(arg(0))
      const bs = list(arg(1))
      const out: { a: unknown, b: unknown }[] = []
      for (const a of as) {
        for (const b of bs) {
          if (out.length >= MAX_EXPR_LIST) break
          out.push({ a, b })
        }
      }
      ctx.steps += out.length
      if (ctx.steps > MAX_EXPR_STEPS) throw new ExprError('expression is too expensive')
      return out
    }
    case 'join': {
      const sep = rawArgs.length > 1 ? str(arg(1)) : ', '
      return list(arg(0)).map(str).filter(v => v !== '').join(sep).slice(0, MAX_EXPR_STRING)
    }
    case 'split': {
      const sep = rawArgs.length > 1 ? str(arg(1)) : ','
      if (sep === '') return []
      return str(arg(0)).split(sep).map(v => v.trim()).filter(v => v !== '').slice(0, MAX_EXPR_LIST)
    }
    case 'fmt': return fmt(arg(0), arg(1), rawArgs.length > 2 ? arg(2) : null, ctx.env)
    case 'today': return isoDate(ctx.env.now ?? Date.now(), ctx.env)
    case 'now': return Math.floor((ctx.env.now ?? Date.now()) / 1000)
    case 'date': return isoDate(arg(0), ctx.env)
    case 'addDays': {
      const base = dateUtc(isoDate(arg(0), ctx.env))
      const n = num(arg(1))
      return base === null || n === null ? null : utcToIso(base + Math.trunc(n) * DAY_MS)
    }
    case 'diffDays': {
      const a = dateUtc(isoDate(arg(0), ctx.env))
      const b = dateUtc(isoDate(arg(1), ctx.env))
      return a === null || b === null ? null : Math.round((b - a) / DAY_MS)
    }
    case 'startOfWeek': {
      const base = dateUtc(isoDate(arg(0), ctx.env))
      if (base === null) return null
      const weekday = (new Date(base).getUTCDay() + 6) % 7
      return utcToIso(base - weekday * DAY_MS)
    }
    case 'weekday': {
      const base = dateUtc(isoDate(arg(0), ctx.env))
      return base === null ? null : (new Date(base).getUTCDay() + 6) % 7 + 1
    }
    case 'minutesOfDay': return minutesOfDay(arg(0), ctx.env)
    case 'timestamp': return zonedTimestamp(arg(0), arg(1), ctx.env)
  }
  return null
}

/** Evaluates an expression; throws ExprError when it is malformed or too expensive. */
export function evalExpr (expr: unknown, scope: ExprScope, env: ExprEnv = {}, budget?: ExprBudget): unknown {
  const value = evaluate(expr, scope, { steps: 0, depth: 0, env, budget })
  return value === undefined ? null : value
}

/** Like evalExpr, but a failing expression yields the fallback instead of throwing. */
export function evalExprSafe<T> (expr: unknown, scope: ExprScope, fallback: T, env: ExprEnv = {}, budget?: ExprBudget): unknown | T {
  try {
    return evalExpr(expr, scope, env, budget)
  } catch {
    return fallback
  }
}
